import debounce from 'lodash/debounce';
import get from 'lodash/get';
import noop from 'lodash/noop';
import set from 'lodash/set';
import unset from 'lodash/unset';

import type { Mutable } from 'Types';

import ConfigService from './ConfigService';
import {
  LEVERS_CONFIG_KEY_CACHED,
  LEVERS_CONFIG_KEY_FALLBACK,
  LEVERS_CONFIG_KEY_LOCKS,
  LEVERS_CONFIG_KEY_OVERRIDE,
  LEVERS_CONFIG_KEY_STORED,
  PERSIST_LEVERS_DEBOUNCE_MS,
  PERSIST_LEVERS_MAX_WAIT_MS,
} from './constants';
import { REGISTERED_LEVER_VALUES } from './levers';
import {
  ConfigServiceLogFunction,
  ExtractLeverValueType,
  LeverConfigStore,
  LeverConfigTuple,
  LeverConfigType,
  LeverKey,
} from './types';
import { inferLeverType, lockIdGen } from './utils';

type CreateConfigServiceOptions = {
  load?: () => Promise<Partial<LeverConfigStore>>;
  save?: (levers: LeverConfigStore) => Promise<void>;
  log?: ConfigServiceLogFunction;
  registeredLeverValues?: { [TLeverKey in LeverKey]: LeverConfigTuple<TLeverKey> };
};

export default async function createConfigService({
  load,
  save,
  log = noop,
  // need to cast REGISTERED_LEVER_VALUES back to mutable to align with the type of registeredLeverValues
  registeredLeverValues = REGISTERED_LEVER_VALUES as Mutable<typeof REGISTERED_LEVER_VALUES>,
}: CreateConfigServiceOptions = {}): Promise<ConfigService> {
  // tiny type lie - levers is only a Partial<LeverConfigStore> for now, but will be
  // initialized with all lever fallbacks after ConfigService is created
  const levers = ((await load?.()) || {}) as LeverConfigStore;

  const throttledSave = debounce(
    async () => {
      await save?.(levers);
    },
    PERSIST_LEVERS_DEBOUNCE_MS,
    { maxWait: PERSIST_LEVERS_MAX_WAIT_MS }
  );

  const configService = new ConfigService(
    {
      getOverride(key) {
        return get(levers, [key, LEVERS_CONFIG_KEY_OVERRIDE, 'value']);
      },
      getCached(key) {
        const lever = get(levers, [key, LEVERS_CONFIG_KEY_CACHED]);
        return lever && [lever.value, lever.pid];
      },
      getStored(key) {
        const lever = get(levers, [key, LEVERS_CONFIG_KEY_STORED]);
        return lever && [lever.value, lever.pid];
      },
      getFallback(key) {
        return get(levers, [key, LEVERS_CONFIG_KEY_FALLBACK, 'value']);
      },
      hasLock(key) {
        const locks = get(levers, [key, 'locks'], {});
        return Object.keys(locks).filter((lockId) => locks[lockId]).length > 0;
      },

      setOverride(key, value) {
        set(levers, [key, LEVERS_CONFIG_KEY_OVERRIDE], { value });
        throttledSave();
      },
      setCached(key, value, pid) {
        set(levers, [key, LEVERS_CONFIG_KEY_CACHED], { value, pid });
      },
      setStored(key, value, pid, type = inferLeverType(value)) {
        const time = Date.now();
        set(levers, [key, LEVERS_CONFIG_KEY_STORED], { value, pid, type, time });
        throttledSave();
      },
      setFallback(key, value, type = inferLeverType(value)): void {
        set(levers, [key, LEVERS_CONFIG_KEY_FALLBACK], { value, type });
      },
      unsetOverride(key) {
        unset(levers, [key, LEVERS_CONFIG_KEY_OVERRIDE]);
        throttledSave();
      },
      unsetCached(key) {
        unset(levers, [key, LEVERS_CONFIG_KEY_CACHED]);
      },

      addLock(key) {
        const lockId = lockIdGen();
        set(levers, [key, LEVERS_CONFIG_KEY_LOCKS, lockId], true);
        return lockId;
      },
      removeLock(key, lockId) {
        unset(levers, [key, LEVERS_CONFIG_KEY_LOCKS, lockId]);
      },
    },
    log
  );

  // Register levers and their fallbacks
  Object.entries(registeredLeverValues).forEach(([key, [value, type = inferLeverType(value)]]) => {
    configService.registerLever(
      key as LeverKey,
      value as ExtractLeverValueType<LeverKey>,
      type as LeverConfigType
    );
  });

  return configService;
}
